import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Header, Footer } from "../components/Orchestrio";
import SEO from "../components/SEO";

const ServicesPage = () => {
  const services = [
    {
      slug: "websites",
      title: "Websites",
      tagline: "High-performance, SEO-ready, conversion-focused",
      description: "Built as maintainable systems, not disposable pages. Your website should load fast, rank well, and convert visitors—without becoming a maintenance burden.",
      forWho: "Businesses that need a professional web presence that actually performs.",
      investment: "£5,000 – £20,000"
    },
    {
      slug: "web-apps",
      title: "Web Applications",
      tagline: "Dashboards, portals, internal tools",
      description: "Designed for real users and long-term use. We build applications that solve specific problems and remain maintainable as your needs evolve.",
      forWho: "Teams that need custom software to run their operations better.",
      investment: "£15,000 – £75,000+"
    },
    {
      slug: "integrations",
      title: "Integrations & Automation",
      tagline: "CRM, payments, analytics, email, APIs",
      description: "Reduce manual work and operational friction. Connect your existing tools, automate repetitive tasks, and eliminate data silos.",
      forWho: "Businesses drowning in manual processes and disconnected systems.",
      investment: "£3,000 – £25,000"
    },
    {
      slug: "mobile-apps",
      title: "Mobile Applications",
      tagline: "MVP to production",
      description: "Clear scope, realistic delivery. We build mobile apps with defined goals and honest timelines—no scope creep, no surprise costs.",
      forWho: "Founders and product teams who need to ship, not endlessly iterate.",
      investment: "£20,000 – £80,000+"
    },
    {
      slug: "support",
      title: "Ongoing Support",
      tagline: "Maintenance, improvements, monitoring",
      description: "Long-term technical partnership. We maintain what we build—and we're available when things go wrong or requirements change.",
      forWho: "Teams that want reliable technical support without hiring full-time.",
      investment: "£500 – £3,000/month"
    }
  ];

  return (
    <div className="min-h-screen bg-[#1E1E1E]" data-testid="services-page">
      <SEO 
        title="Services"
        description="Digital systems designed for clarity and built for maintainability. Websites, web applications, integrations, mobile apps, and ongoing support for UK businesses."
      />
      <Header />

      {/* Header */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-20">
        <div className="max-w-5xl mx-auto px-6">
          <p className="text-[#5F8FB8] text-sm font-medium tracking-wide mb-6">
            Services
          </p>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-6">
            Systems that solve problems.
          </h1>
          <p className="text-lg text-[#9CA3AF] max-w-2xl leading-relaxed">
            Every project we take on is approached as a system—designed for clarity, 
            built for maintainability, and delivered with proper documentation.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="pb-20 border-t border-[#2E2E2E]">
        <div className="max-w-5xl mx-auto px-6 pt-16">
          <div className="space-y-8">
            {services.map((service, index) => (
              <Link 
                key={index}
                to={`/services/${service.slug}`}
                className="block group"
              >
                <div className="p-8 bg-[#242424] border border-[#2E2E2E] hover:border-[#3A3A3A] transition-colors">
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                    <div className="flex-1">
                      <div className="flex items-baseline gap-3 mb-3">
                        <h2 className="text-xl font-bold group-hover:text-[#5F8FB8] transition-colors">
                          {service.title}
                        </h2>
                        <span className="text-[#5F8FB8] text-sm">{service.tagline}</span>
                      </div>
                      <p className="text-[#9CA3AF] leading-relaxed mb-4 max-w-2xl">
                        {service.description}
                      </p>
                      <p className="text-sm text-[#6B7280]">
                        <span className="text-[#9CA3AF]">Best for:</span> {service.forWho}
                      </p>
                    </div>
                    <div className="lg:text-right flex-shrink-0">
                      <p className="text-sm text-[#6B7280] mb-1">Typical investment</p>
                      <p className="font-semibold">{service.investment}</p>
                      <span className="inline-flex items-center gap-1 text-[#5F8FB8] text-sm mt-3 group-hover:underline">
                        Learn more <ArrowRight className="w-3 h-3" />
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Approach Section */}
      <section className="py-16 bg-[#242424] border-t border-[#2E2E2E]">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl font-bold mb-8">Our approach</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="font-semibold mb-2">We start with the problem</h3>
              <p className="text-sm text-[#9CA3AF] leading-relaxed">
                Before recommending a solution, we understand what you're actually trying to achieve. 
                Sometimes the answer isn't what you initially thought.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-2">We scope before we build</h3>
              <p className="text-sm text-[#9CA3AF] leading-relaxed">
                Every project gets a detailed specification. You'll know exactly what we're building, 
                how long it will take, and what it will cost—before any code is written.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-2">We build for the long term</h3>
              <p className="text-sm text-[#9CA3AF] leading-relaxed">
                Systems should be maintainable, not just functional. We write clean code, 
                use standard technologies, and document everything properly.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-2">We stay accountable</h3>
              <p className="text-sm text-[#9CA3AF] leading-relaxed">
                Weekly updates. Clear milestones. No vanishing after launch. 
                We take ownership of what we build.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 border-t border-[#2E2E2E]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8">
            <div>
              <h2 className="text-2xl font-bold mb-3">Not sure where to start?</h2>
              <p className="text-[#9CA3AF] max-w-lg">
                Get in touch. We'll help you figure out what you actually need—and 
                whether we're the right team to build it.
              </p>
            </div>
            <Link to="/contact" className="btn-primary inline-flex items-center gap-2 flex-shrink-0">
              Get in touch <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicesPage;
